import React, { useState, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

export default function SettingsPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // ✅ State for form fields
  const [hotelName, setHotelName] = useState('Transcorp Hilton International');
  const [email, setEmail] = useState('admin@transcorphilton.com');
  const [phone, setPhone] = useState('+234 801 234 5678');
  const [address, setAddress] = useState('1 Aguiyi Ironsi St, Maitama, Abuja');
  const [notifications, setNotifications] = useState(true);

  // ✅ Logo upload state
  const [logoPreview, setLogoPreview] = useState(require('../assets/user1.png'));
  const [logoFile, setLogoFile] = useState(null);

  // ✅ Save button & toast state
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // 📌 Handle logo selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file)); // 🔥 Instant preview
    }
  };

  // 📌 Handle save changes
  const handleSaveChanges = () => {
    setIsSaving(true);

    // Simulate API call (2 sec delay)
    setTimeout(() => {
      setIsSaving(false);
      setShowToast(true); // ✅ Show toast on save
      setTimeout(() => setShowToast(false), 3000); // Auto-hide after 3 sec
    }, 2000);
  };

  return (
    <div className="d-flex vh-100 overflow-hidden">
      
      {/* ✅ Sidebar */}
      <Sidebar 
        active="settings"
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
      />

      {/* ✅ Main Content */}
      <div className="flex-grow-1 d-flex flex-column bg-light">
        
        {/* ✅ Header */}
        <Header 
          darkMode={darkMode} 
          setDarkMode={setDarkMode} 
          toggleSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />

        {/* ✅ Toast Notification */}
        {showToast && (
          <div 
            className="toast-container position-fixed top-0 end-0 p-3"
            style={{ zIndex: 1055 }}
          >
            <div className="toast show align-items-center text-bg-success border-0">
              <div className="d-flex">
                <div className="toast-body">
                  ✅ Changes saved successfully!
                </div>
                <button 
                  type="button" 
                  className="btn-close btn-close-white me-2 m-auto" 
                  onClick={() => setShowToast(false)}
                ></button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Scrollable Main Area */}
        <main className="flex-grow-1 overflow-auto p-4">

          {/* 📌 PAGE TITLE */}
          <h3 className="fw-bold mb-4 cool-font">Settings & Profile</h3>

          <div className="row g-4">

            {/* ✅ Profile Card */}
            <div className="col-lg-4">
              <div className="p-4 rounded bg-white border text-center">
                <div 
                  className="mx-auto rounded-circle overflow-hidden"
                  style={{ width: '120px', height: '120px', background: '#f0f0f0' }}
                >
                  <img 
                    src={logoPreview} 
                    alt="Hotel Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                </div>

                <h5 className="fw-bold mt-3">{hotelName}</h5>
                <p className="text-muted small mb-3">{email}</p>

                {/* ✅ Hidden file input */}
                <input 
                  type="file" 
                  accept="image/*" 
                  id="logoUpload" 
                  className="d-none" 
                  onChange={handleLogoChange} 
                />
                
                {/* ✅ Upload Button triggers hidden file input */}
                <button 
                  className="btn btn-outline-primary btn-sm w-100"
                  onClick={() => document.getElementById('logoUpload').click()}
                >
                  {logoFile ? 'Change Logo' : 'Upload New Logo'}
                </button>
              </div>
            </div>

            {/* ✅ Settings Form */}
            <div className="col-lg-8">
              <div className="p-4 rounded bg-white border">
                <h5 className="fw-bold mb-3">Hotel Information</h5>
                
                <div className="mb-3">
                  <label className="form-label fw-semibold">Hotel Name</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={hotelName} 
                    onChange={(e) => setHotelName(e.target.value)} 
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Email</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Phone Number</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={phone} 
                    onChange={(e) => setPhone(e.target.value)} 
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Address</label>
                  <textarea 
                    className="form-control" 
                    rows="2" 
                    value={address} 
                    onChange={(e) => setAddress(e.target.value)}
                  ></textarea>
                </div>
              </div>

              {/* ✅ Password Reset Section */}
              <div className="p-4 rounded bg-white border mt-4">
                <h5 className="fw-bold mb-3">Password Management</h5>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Current Password</label>
                  <input type="password" className="form-control" placeholder="Enter current password" />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">New Password</label>
                  <input type="password" className="form-control" placeholder="Enter new password" />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Confirm New Password</label>
                  <input type="password" className="form-control" placeholder="Confirm new password" />
                </div>
              </div>

              {/* ✅ Notification Preferences */}
              <div className="p-4 rounded bg-white border mt-4">
                <h5 className="fw-bold mb-3">Notification Preferences</h5>
                <div className="form-check form-switch">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="notifications" 
                    checked={notifications} 
                    onChange={() => setNotifications(!notifications)}
                  />
                  <label className="form-check-label" htmlFor="notifications">
                    Receive email notifications for new verifications and reports
                  </label>
                </div>
              </div>

              {/* ✅ Save Changes Button */}
              <div className="mt-4">
                <button 
                  className="btn btn-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                >
                  {isSaving && (
                    <span 
                      className="spinner-border spinner-border-sm" 
                      role="status" 
                      aria-hidden="true"
                    ></span>
                  )}
                  {isSaving ? 'Saving Changes…' : 'Save Changes'}
                </button>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}
